import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';

import { ProjectService } from 'src/app/shared/project/project.service';
import { GoogleDriveService } from './google-drive.service';
import { DataService } from 'src/app/shared/network/data.service';
import { GoogleSettingsService } from './google-settings.service';
import { ImportProgressService } from 'src/app/shared/import/import-progress.service';
import { NotificationService } from 'src/app/shared/notificaton.service';
import { ImportErrorDesc } from '../import-error-desc';
import { ColumnScrollService } from '../column-scroll.service';
import { TagItem } from 'src/app/shared/project/tag-item';
import { DeleteTagModalComponent } from './delete-tag-modal.component';

enum UploadStatus {
    None,
    InProgress,
    Error,
    Warn,
    Success,
};

@Component({
    templateUrl: './upload-file-modal.component.html',
    styleUrls: ['./upload-file-modal.component.scss']
})
export class UploadFileModalComponent implements OnInit, AfterViewInit, OnDestroy {
    // The bar: sending the file up to 70%, the server's handling of it up to 90%, the data sanity check up to 100%
    private static readonly SENT_PERCENT = 70;
    private static readonly HANDLED_PERCENT = 90;
    // The server's handling reports nothing and takes seconds (it parses the file and waits for the AI's guess of the column types,
    // see ColumnSelectionService.SetColumnTypesFromNamedRanges): meanwhile the bar creeps towards 90%, slower and slower, through at
    // most this share of the way
    private static readonly MAX_HANDLING_CREEP = 0.9;
    // The creep's time constant, in which it covers 63% of what it may
    private static readonly HANDLING_CREEP_TIME_MS = 5000;

    UploadStatus = UploadStatus;
    private googleDriveService: GoogleDriveService;
    fileToUpload: File = null;
    filename: string = '';
    fileSizeKB: string;
    projectName: string = '';
    googleDocUrl: string;
    uploadPerc: number = 0;
    private handlingCreepFrameId: number | null = null;
    loading: boolean = false;
    uploadStatus = UploadStatus.None;

    @ViewChild('tableContainer', { static: false }) tableContainer: ElementRef;

    private _projectId: string | undefined;     //do not set directly, use projectId setter
    get projectId(): string | undefined {
        return this._projectId;
    }
    set projectId(value: string | undefined) {
        if (this._projectId !== undefined) {
            throw new Error("projectId can only be set once.");
        }
        this._projectId = value;
    }
    public editing = false;

    //data sanity:
    columnNames: string [];
    columnUniqueValsCounts: number[];
    blockingIssues: ImportErrorDesc[];
    standaloneWarnings: ImportErrorDesc[];
    columnWarnings: ImportErrorDesc[][];
    public btnGoToWizardEnabled: boolean;

    selectedTags: string[] = [];
    newTagInput: string = '';
    isTagsDropdownOpen: boolean = false;
    allTags: TagItem[] = [];

    @Output() projectAdded = new EventEmitter<string>();
    @Output() tagDeleted = new EventEmitter<string>();

    constructor(@Inject(MAT_DIALOG_DATA) data: any, private dataService: DataService, private router: Router, notifier: NotificationService, private ngZone: NgZone,
        @Inject(DOCUMENT) document, elementRef: ElementRef, googleSettingsService: GoogleSettingsService, private projectService: ProjectService, private dialogRef: MatDialogRef<UploadFileModalComponent>, private columnScrollService: ColumnScrollService, private dialog: MatDialog,
        private changeDetector: ChangeDetectorRef) {
        this.googleDriveService = new GoogleDriveService(notifier, this.filePickedCallback.bind(this), document, elementRef, googleSettingsService);
        this.btnGoToWizardEnabled = false;
        if (data) {
            if (data.projectId) {
                this.projectId = data.projectId;
                this.editing = true;
            }
            if (data.allTags) {
                this.allTags = data.allTags;
            }
        }
    }

    async ngOnInit(): Promise<void> {
        if (this.editing && this.projectId) {
            this.projectService.getProjectListItem(this.projectId).then((projectListItem) => {
                this.projectName = projectListItem.name;
                this.selectedTags = projectListItem.tags || [];
            })
        }
    }
    
    ngAfterViewInit(): void {
        this.googleDriveService.addScriptInclude();
    }

    ngOnDestroy(): void {
        this.stopHandlingCreep();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const clickedInside = target.closest('.tags-select');
        if (!clickedInside && this.isTagsDropdownOpen) {
            this.isTagsDropdownOpen = false;
        }
    }

    async handleFileInput(files: FileList) {
        if (files.length > 0) {
            this.fileToUpload = files.item(0);
            this.filename = files.item(0).name;
            this.fileSizeKB = (files[0].size / 1024).toFixed(1);
            if (!this.projectName) {
                this.projectName = this.removeExtension(this.filename);
            }
            if (!this.editing) {
                await this.uploadFile();
            }
        }
    }

    public async uploadFile() {
        if (!this.fileToUpload) {
            return;
        }
        if (this.loading){
            return;
        }
        try {
            this.loading = true;
            this.uploadPerc = 0;
            this.uploadStatus = UploadStatus.InProgress;
            if (this.editing) {
                await this.dataService.postFile('reupload', this.fileToUpload, {
                        projectId: this.projectId,
                        tags: this.selectedTags,
                    }, (progress) => {
                        this.uploadProgress(progress);
                    });
            } else {
                this.projectId = await this.dataService.postFile('upload', this.fileToUpload, {
                    projectName: this.projectName,
                        tags: this.selectedTags,
                 }, (progress) => {
                    this.uploadProgress(progress);
                });
            }
            await this.onUploadComplete();
        } catch {
            this.uploadStatus = UploadStatus.Error;
        } finally {
            this.stopHandlingCreep();
            this.loading = false;
        }
    }
    
    private uploadProgress(progress: number): void {
        this.uploadPerc = UploadFileModalComponent.SENT_PERCENT * progress / 100;
        if (progress >= 100) {
            this.startHandlingCreep();
        }
    }

    private startHandlingCreep(): void {
        if (this.handlingCreepFrameId !== null) {
            return;
        }
        const start = performance.now();
        const frame = () => {
            const creep = 1 - Math.exp((start - performance.now()) / UploadFileModalComponent.HANDLING_CREEP_TIME_MS);
            this.uploadPerc = UploadFileModalComponent.SENT_PERCENT + (UploadFileModalComponent.HANDLED_PERCENT - UploadFileModalComponent.SENT_PERCENT)
                * UploadFileModalComponent.MAX_HANDLING_CREEP * creep;
            this.changeDetector.detectChanges();
            this.handlingCreepFrameId = requestAnimationFrame(frame);
        };
        // Outside the zone, so that a frame checks only this dialog, not the project list under it
        this.ngZone.runOutsideAngular(() => {
            this.handlingCreepFrameId = requestAnimationFrame(frame);
        });
    }

    private stopHandlingCreep(): void {
        if (this.handlingCreepFrameId !== null) {
            cancelAnimationFrame(this.handlingCreepFrameId);
            this.handlingCreepFrameId = null;
        }
    }

    private removeExtension(filename: string): string {
        const lastDotIx = filename.lastIndexOf(".");
        if (lastDotIx === -1) {
            return filename;
        }
        return filename.substring(0, lastDotIx);
    }

    private async onUploadComplete() {
        this.stopHandlingCreep();
        this.fileToUpload = null;
        this.uploadPerc = UploadFileModalComponent.HANDLED_PERCENT;
        if (this.filename.endsWith('.dwp')) {
            this.router.navigateByUrl('/cards?projectId=' + this.projectId);
            this.dialogRef.close();
        } else {
            this.loading = true;
            let dataSanityCheckResult: any = await this.projectService.getDataSanityCheckResult(this.projectId);
            this.uploadPerc = 100;

            const hasBlockingIssues = dataSanityCheckResult.blockingIssues && dataSanityCheckResult.blockingIssues.length > 0;
            const hasStandaloneWarnings = dataSanityCheckResult.warnings &&
                              dataSanityCheckResult.warnings.standaloneWarnings &&
                              dataSanityCheckResult.warnings.standaloneWarnings.length > 0;

            if (!hasBlockingIssues && !hasStandaloneWarnings) {
                let hasWarn = false;
                for (let i = 0; i < dataSanityCheckResult.warnings.columnWarnings.length; ++i) {
                    if (dataSanityCheckResult.warnings.columnWarnings[i].length) {
                        hasWarn = true;
                        break;
                    }
                }
                if (!hasWarn) {
                    this.uploadStatus = UploadStatus.Success;   //probably won't be seen, but still
                    this.goToWizardInternal();
                    return;
                }
            }

            this.blockingIssues = dataSanityCheckResult.blockingIssues;
            this.columnNames = dataSanityCheckResult.columnNames;
            this.columnUniqueValsCounts = dataSanityCheckResult.columnUniqueValsCounts;
            if (dataSanityCheckResult.warnings){
                this.standaloneWarnings = dataSanityCheckResult.warnings.standaloneWarnings;
                this.columnWarnings = dataSanityCheckResult.warnings.columnWarnings;
            }
            this.loading = false;
            this.btnGoToWizardEnabled = !hasBlockingIssues;
            this.uploadStatus = hasBlockingIssues ? UploadStatus.Error : UploadStatus.Warn;
            this.projectAdded.emit(this.projectId); //to update the project list page

            // Scroll to first column warning if any exist
            if (this.columnWarnings && this.columnWarnings.length > 0) {
                setTimeout(() => {
                    if (this.tableContainer) {
                        this.columnScrollService.scrollToFirstColumnWarning(this.tableContainer, this.columnWarnings);
                    }
                }, 200); // Allow time for the view to update
            }
        }

    }

    public googleDocUrlIsValid() {
        return this.googleDocUrl && this.googleDocUrl.startsWith('https://docs.google.com/spreadsheets/d/');
    }

    pickAndDownloadFile() {
        this.googleDriveService.pickAndDownloadFile();
    }

    private filePickedCallback(fileId, accessToken, fileName, mimeType) {
        let self = this;
        this.dataService.post<string>('upload/createFromPrivateGoogleDoc', {
            fileId: fileId,
            accessToken: accessToken,
            fileName: fileName,
            mimeType: mimeType,
        }).then(projectId => {
            self.ngZone.run(() => {
                self.projectId = projectId;
                self.onUploadComplete();
            });
        });
    }

    get xlsIcon(): string {
        switch (this.uploadStatus) {
          case UploadStatus.Error:
            return 'assets/images/icon-xls-error.svg';
          case UploadStatus.Warn:
            return 'assets/images/icon-xls-warning.svg';
          default:
            return 'assets/images/icon-xls.svg';
        }
      }

    async updateProjectMeta() {
        if (!this.projectName || !this.projectId) {
            return;
        }
        try {
            this.loading = true;
            await this.projectService.updateProjectMeta(this.projectId, this.projectName, this.selectedTags);
            this.dialogRef.close(true);
        } finally {
            this.loading = false;
        }
    }

    goToWizard() {
        if (this.btnGoToWizardEnabled && !this.loading) {
            this.goToWizardInternal();
        }
    }

    goToWizardInternal() {
        this.loading = true;
        this.router.navigateByUrl(`/import-wizard?projectId=${this.projectId}&step=ControlColumn`);
        this.dialogRef.close();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault(); // Prevent default behavior
        event.stopPropagation();
    }
    
    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    
        if (event.dataTransfer?.files.length) {
            const fileInput = document.getElementById("file") as HTMLInputElement;
            const files = event.dataTransfer.files;
    
            const dataTransfer = new DataTransfer();
            for (let i = 0; i < files.length; i++) {
                dataTransfer.items.add(files[i]);
            }
            fileInput.files = dataTransfer.files;
    
            this.handleFileInput(fileInput.files);
        }
    }

    removeTag(tag: string) {
        const index = this.selectedTags.indexOf(tag);
        if (index >= 0) {
            this.selectedTags.splice(index, 1);
        }
    }

    toggleTag(tag: string) {
        const index = this.selectedTags.indexOf(tag);
        if (index >= 0) {
            this.selectedTags.splice(index, 1);
        } else {
            this.selectedTags.push(tag);
        }
    }

    isTagSelected(tag: string): boolean {
        return this.selectedTags.indexOf(tag) >= 0;
    }

    addNewTag() {
        if (this.newTagInput && this.newTagInput.trim()) {
            const newTag = this.newTagInput.trim();
            if (!this.allTags.find(t => t.name === newTag)) {
                this.allTags.push({ name: newTag, usedIn: 0 });
            }
            if (!this.selectedTags.includes(newTag)) {
                this.selectedTags.push(newTag);
            }
            this.newTagInput = '';
        }
    }

    toggleTagsDropdown() {
        this.isTagsDropdownOpen = !this.isTagsDropdownOpen;
    }

    deleteTag(tag: string) {
        const tagItem = this.allTags.find(t => t.name === tag);
        if (!tagItem) return;

        if (tagItem.usedIn > 0) {
            const dialogRef = this.dialog.open(DeleteTagModalComponent, {
                data: { tagName: tag, usedInCount: tagItem.usedIn }
            });

            dialogRef.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.performTagDeletion(tag);
                }
            });
        } else {
            this.performTagDeletion(tag);
        }
    }

    private async performTagDeletion(tagName: string) {
        const tagIndex = this.allTags.findIndex(t => t.name === tagName);
        if (tagIndex >= 0) {
            this.allTags.splice(tagIndex, 1);
        }
        const selectedIndex = this.selectedTags.indexOf(tagName);
        if (selectedIndex >= 0) {
            this.selectedTags.splice(selectedIndex, 1);
        }
        await this.dataService.post('Projects/DeleteTag', { tagName: tagName });
        this.tagDeleted.emit(tagName);
    }
}
